import { isValidEmail, isValidPhone } from "../../../../utils/utils";
import { FORM_VALIDATION_MSG, MAX_LENGTH_CONSTRAINTS } from "../constants";

export const REQUESTOR_API_FIELDS = {
  NAME: "requestorName",
  COMPANY: "requestorCompany",
  PHONE: "requestorPhone",
  EMAIL: "requestorEmail",
};

export const normalizePhoneDigits = (phone) =>
  String(phone ?? "").replace(/\D/g, "");

export const getDefaultRequestorInfo = (pickup) => ({
  [REQUESTOR_API_FIELDS.NAME]: pickup?.[REQUESTOR_API_FIELDS.NAME] ?? "",
  [REQUESTOR_API_FIELDS.COMPANY]: pickup?.[REQUESTOR_API_FIELDS.COMPANY] ?? "",
  [REQUESTOR_API_FIELDS.PHONE]:
    pickup?.[REQUESTOR_API_FIELDS.PHONE] != null
      ? normalizePhoneDigits(pickup[REQUESTOR_API_FIELDS.PHONE])
      : "",
  [REQUESTOR_API_FIELDS.EMAIL]: pickup?.[REQUESTOR_API_FIELDS.EMAIL] ?? "",
});

export const getRequestorFieldError = (field, value) => {
  const maxLen =
    MAX_LENGTH_CONSTRAINTS.CREATE_PICKUP_FORM.RESERVATION_REQUESTOR_TEXT;
  const maxLenMsg = `* Max ${maxLen} characters`;
  const F = REQUESTOR_API_FIELDS;

  switch (field) {
    case F.NAME: {
      const name = String(value ?? "").trim();
      if (!name) return null;
      if (name.length > maxLen) return maxLenMsg;
      return null;
    }
    case F.COMPANY: {
      const company = String(value ?? "").trim();
      if (!company) return null;
      if (company.length > maxLen) return maxLenMsg;
      return null;
    }
    case F.PHONE: {
      const digits = normalizePhoneDigits(value);
      if (!digits) return null;
      if (!isValidPhone(digits)) return FORM_VALIDATION_MSG.INVALID_PHONE_NUMBER;
      return null;
    }
    case F.EMAIL: {
      const email = String(value ?? "").trim();
      if (!email) return null;
      if (!isValidEmail(email)) return FORM_VALIDATION_MSG.INVALID_EMAIL;
      return null;
    }
    default:
      return null;
  }
};

export const getDefaultRequestorErrorInfo = () => ({
  [REQUESTOR_API_FIELDS.NAME]: null,
  [REQUESTOR_API_FIELDS.COMPANY]: null,
  [REQUESTOR_API_FIELDS.PHONE]: null,
  [REQUESTOR_API_FIELDS.EMAIL]: null,
});

export const getRequestorErrorInfo = (values) => {
  const F = REQUESTOR_API_FIELDS;
  return {
    [F.NAME]: getRequestorFieldError(F.NAME, values?.[F.NAME]),
    [F.COMPANY]: getRequestorFieldError(F.COMPANY, values?.[F.COMPANY]),
    [F.PHONE]: getRequestorFieldError(F.PHONE, values?.[F.PHONE]),
    [F.EMAIL]: getRequestorFieldError(F.EMAIL, values?.[F.EMAIL]),
  };
};

const toNullableRequestorText = (value) => {
  const trimmed = String(value ?? "").trim();
  return trimmed || null;
};

const toNullableRequestorPhone = (value) => {
  const digits = normalizePhoneDigits(value);
  return digits || null;
};

export const mapRequestorToRequest = (info) => {
  if (!info) {
    return {
      [REQUESTOR_API_FIELDS.NAME]: null,
      [REQUESTOR_API_FIELDS.COMPANY]: null,
      [REQUESTOR_API_FIELDS.PHONE]: null,
      [REQUESTOR_API_FIELDS.EMAIL]: null,
    };
  }
  return {
    [REQUESTOR_API_FIELDS.NAME]: toNullableRequestorText(
      info[REQUESTOR_API_FIELDS.NAME]
    ),
    [REQUESTOR_API_FIELDS.COMPANY]: toNullableRequestorText(
      info[REQUESTOR_API_FIELDS.COMPANY]
    ),
    [REQUESTOR_API_FIELDS.PHONE]: toNullableRequestorPhone(
      info[REQUESTOR_API_FIELDS.PHONE]
    ),
    [REQUESTOR_API_FIELDS.EMAIL]: toNullableRequestorText(
      info[REQUESTOR_API_FIELDS.EMAIL]
    ),
  };
};

export const hasAnyRequestorValue = (requestorInfo) =>
  Object.values(mapRequestorToRequest(requestorInfo)).some(Boolean);

export const getReservationDetailsPayload = (enableRequestorInfo, requestorInfo) => {
  if (!enableRequestorInfo) {
    return null;
  }
  const mapped = mapRequestorToRequest(requestorInfo);
  return hasAnyRequestorValue(mapped) ? mapped : null;
};

